import { useCallback, useEffect, useRef, useState } from 'react';
import { useStateContext } from '../../modules/stateContext';
import { sendCustomEvent } from './sendCustomEvent';
import { SINGLE_ENTRY_AUTH_UK_EXPERIMENT } from '../../../shared/data/mvtExperiments';

type SendCustomEventOptions = Parameters<typeof sendCustomEvent>[0];

type UseSendCustomEventCallbackOptions = Omit<
    SendCustomEventOptions,
    'featureToggles' | 'optimizelyVariantName'
> &
    Partial<SendCustomEventOptions>;

export function useSendCustomEvent() {
    const [success, setSuccess] = useState<boolean>();
    const mounted = useRef<boolean>();

    const [isLoading, setIsLoading] = useState(false);

    const {
        featureToggles: {
            generateUserOriginFromPtrt,
            reverbUserOrigin,
            enableSingleAuthEntryUk,
        },
        location: { isUk },
        isSingleEntryAuth,
        policy,
        isFederated,
    } = useStateContext();

    const variantName = () => {
        if (
            enableSingleAuthEntryUk &&
            policy === 'tv' &&
            isUk &&
            !isFederated
        ) {
            if (isSingleEntryAuth)
                return SINGLE_ENTRY_AUTH_UK_EXPERIMENT.VARIATION
                    .SINGLE_AUTH_ENTRY;

            return SINGLE_ENTRY_AUTH_UK_EXPERIMENT.VARIATION.CONTROL;
        }

        return undefined;
    };

    const callSendCustomEvent = useCallback(
        async (options: UseSendCustomEventCallbackOptions) => {
            setIsLoading(true);

            const result = await sendCustomEvent({
                generateUserOriginFromPtrt,
                reverbUserOrigin,
                optimizelyVariantName: variantName(),
                ...options,
            });

            if (mounted.current) {
                setSuccess(result);
                setIsLoading(false);
            }
        },
        [generateUserOriginFromPtrt, reverbUserOrigin]
    );

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    });

    return {
        isLoading,
        sendCustomEvent: callSendCustomEvent,
        success,
    };
}
